/**
 * T103 (GAP-1 closed). Structure copied from `../products/columns.tsx`, this migration's own
 * precedent for a catalogue list column set — `SkuListItemView` denormalizes `brandName`/
 * `categoryName` itself, so unlike products this needs no join map built by the loader.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Badge as DomainBadge } from "@steelyard/theme";
import { Flex, Text } from "rizzui";
import { Link } from "react-router-dom";
import { PiEyeDuotone as EyeIcon, PiPencilSimpleDuotone as PencilIcon } from "react-icons/pi";
import type { SkuListItem } from "../../../lib/services/catalog";
import { RowActions } from "../../../components/table/row-actions";
import { StatusToggle } from "../../../components/table/status-toggle";
import { skuPath } from "../../../lib/paths";

export function skuColumns(setStatus?: (sku: SkuListItem, active: boolean) => Promise<unknown>): ColumnDef<SkuListItem>[] {
  return [
    {
      id: "code",
      accessorKey: "code",
      header: "Code",
      size: 160,
      // The code opens the read-only view; the row menu's Edit SKU opens the form.
      cell: ({ row }) => (
        <Link to={`${skuPath(row.original.slug)}/view`} className="font-mono text-sm hover:underline">
          {row.original.code}
        </Link>
      ),
    },
    {
      id: "name",
      accessorKey: "name",
      header: "Name",
      size: 260,
    },
    {
      id: "brand",
      accessorKey: "brandName",
      header: "Brand",
      size: 160,
    },
    {
      id: "category",
      accessorKey: "categoryName",
      header: "Category",
      size: 160,
    },
    {
      id: "status",
      accessorKey: "isActive",
      header: "Status",
      size: 220,
      // Two independent facts, both the API's own — isActive and isIncomplete (FR-013) — never
      // one badge standing in for both.
      cell: ({ row }) => (
        <Flex align="center" gap="2">
          <StatusToggle name={row.original.name} active={row.original.isActive} onChange={setStatus && ((next) => setStatus(row.original, next))} />
          {row.original.isIncomplete ? <DomainBadge kind="incomplete" /> : null}
        </Flex>
      ),
    },
    {
      id: "units",
      accessorKey: "sellableUnitCount",
      header: "Sellable units",
      size: 130,
      meta: { align: "right" },
      cell: ({ getValue }) => <Text className="tabular-nums">{getValue<number>()}</Text>,
    },
    {
      id: "actions",
      header: "",
      size: 90,
      cell: ({ row }) => (
        <RowActions
          name={row.original.name}
          primary={
            setStatus
              ? { label: "Edit SKU", icon: PencilIcon, to: skuPath(row.original.slug) }
              : { label: "View SKU", icon: EyeIcon, to: `${skuPath(row.original.slug)}/view` }
          }
        />
      ),
    },
  ];
}
